import { Validate } from '@/lib/useValidate';
import { useStore } from 'vuex';
const store = useStore();

const INPUT_ERROR_MSG = '영문, 숫자 및 특수문자(,./-_()#~:;&<>@)만 입력 가능합니다.';
const validate = new Validate();
export declare namespace ModelEms {
  /**
   * EMS 보내는분 정보
   */
  interface Origin {
    [key: string]: any;

    name: string;
    phoneNumber: string;
    jibunAddress: string;
    roadAddress: string;
    detailAddress: string;
    postalCode: string;
    email: string | null;
  }
  /**
   * EMS 받는분 정보
   */
  interface Destination {
    [key: string]: any;

    name: string;
    phoneNumberCountryCode: string;
    phoneNumberAreaCode: string;
    phoneNumber1: string;
    phoneNumber2: string;
    country: string;
    countryCode: string;
    state: string;
    city: string;
    postalCode: string;
    jibunAddress: string;
    roadAddress: string;
    email: string | null;
  }
  /**
   * EMS 물품 구분
   */
  interface CustomsDeclaration {
    [key: string]: any;

    category: 'GIFT' | 'MERCHANDISE' | 'SAMPLE' | 'DOCUMENT';
    hsTariffNumber: string;
    quantity: number;
    netWeight: number;
    detailedDescriptionOfContents: string;
    value: string;
    countryOfOrigin: string;
  }
  /**
   * EMS 보험 취급
   */
  interface Insurance {
    agreement: boolean;
    insuranceValue: number;
  }
}
declare namespace ModelEmsPayload {
  interface OutboundEms {
    origin: ModelEms.Origin;
    destination: ModelEms.Destination;
    customsDeclaration: ModelEms.CustomsDeclaration;
    insurance: ModelEms.Insurance;
    active: boolean;
    clientComment: string;
    customFields: outbound.customFields[];
  }
}

declare namespace ModelEmsResponse {
  interface packageList {
    category: 'ems' | 'dhl';
    fullNumber: string;
    notifiedPrice: number;
    origin: ModelEms.Origin;
    destination: ModelEms.Destination;
    customsDeclaration: ModelEms.CustomsDeclaration;
    insurance: ModelEms.Insurance;
  }
}

/**
 * 국제우편 EMS 데이터 모델
 * @category 국제우편 -> EMS
 */
export class ModelEms {
  origin: ModelEms.Origin = {
    name: '',
    phoneNumber: '',
    jibunAddress: '',
    roadAddress: '',
    detailAddress: '',
    postalCode: '',
    email: null,
  };
  destination: ModelEms.Destination = {
    name: '',
    phoneNumberCountryCode: '',
    phoneNumberAreaCode: '',
    phoneNumber1: '',
    phoneNumber2: '',
    country: '',
    countryCode: '',
    state: '',
    city: '',
    postalCode: '',
    jibunAddress: '',
    roadAddress: '',
    email: null,
  };
  customsDeclaration: ModelEms.CustomsDeclaration = {
    category: 'GIFT',
    hsTariffNumber: '',
    quantity: 0,
    netWeight: 0,
    detailedDescriptionOfContents: '',
    value: '',
    countryOfOrigin: '',
  };
  insurance: ModelEms.Insurance = {
    agreement: false,
    insuranceValue: 0,
  };
  clientComment: string = '';
  isValidate?: boolean = false;
  customFields: outbound.customFields[] = [];
  customerDropOffMethod: outbound.itemDeliveryMethod = '';
  onsitePickupRequestedAt: string = '';
  onsitePickupClientComment: string = '';

  validateCustomFields(index: string) {
    for (const customField of this.customFields) {
      if (customField.name === index && !customField.value && this.isValidate) return `${index} 정보를 입력해 주세요.`;
    }

    return undefined;
  }

  get validateOriginName() {
    const test = validate.isEngInput(this.origin.name);
    if (!this.origin.name && this.isValidate) return '이름을 입력해 주세요.';
    if (!test) return INPUT_ERROR_MSG;
    return undefined;
  }

  get validateOriginPhoneNumber() {
    const numberTest = validate.isOnlyNumber(this.origin.phoneNumber);
    const lengthTest = this.origin.phoneNumber.length < 8 ? false : true;
    if (!this.origin.phoneNumber && !this.isValidate) return undefined;
    if (!this.origin.phoneNumber && this.isValidate) return '전화번호를 입력해 주세요.';
    if (!numberTest) return '숫자만 입력 가능합니다.';
    if (!lengthTest) return '전화번호는 8자리 이상입니다.';
    return undefined;
  }

  get validateOriginPostalCode() {
    if (!this.origin.postalCode && this.isValidate) return '우편번호를 입력해 주세요.';
    return undefined;
  }

  get validateOriginJibunAddress() {
    const test = validate.isEngInput(this.origin.jibunAddress);
    if (!this.origin.jibunAddress && this.isValidate) return '주소를 입력해 주세요.';
    if (!test) return INPUT_ERROR_MSG;
    return undefined;
  }

  get validateOriginDetailAddress() {
    const test = validate.isEngInput(this.origin.detailAddress);
    if (!this.origin.detailAddress && this.isValidate) return '상세주소를 입력해 주세요.';
    if (!test) return INPUT_ERROR_MSG;
    return undefined;
  }

  get validateOriginEmail() {
    const store = useStore();

    if (store) {
      const globalCategory = store.state.outboundGlobalMail.ems.category === 'dhl';
      if (!this.origin.email && globalCategory && this.isValidate) return '이메일을 입력해 주세요.';
    }

    if (!this.origin.email) return undefined;
    const test = validate.isEmail(this.origin.email);
    if (!test) return '올바른 이메일 주소를 입력해 주세요.';
    return undefined;
  }

  get validateDestinationName() {
    const test = validate.isEngInput(this.destination.name);
    if (!this.destination.name && this.isValidate) return '이름을 입력해 주세요.';
    if (!test) return INPUT_ERROR_MSG;
    return undefined;
  }

  get validateDestinationCountry() {
    const test = validate.isOnlyKor(this.destination.country);
    if (!this.destination.country && this.isValidate) return '배송국가를 입력해 주세요.';
    if (!test) return '한글만 입력 가능합니다.';
    return undefined;
  }

  get validateDestinationCountryCode() {
    const test = validate.isOnlyUpperEng(this.destination.countryCode);
    if (!this.destination.countryCode && this.isValidate) return '국가코드를 입력해 주세요.';
    if (!test) return '영문 대문자만 입력 가능합니다.';
  }

  get validateDestinationState() {
    const test = validate.isEngInput(this.destination.state);
    if (!this.destination.state && this.isValidate) return '도시명을 입력해 주세요.';
    if (!test) return INPUT_ERROR_MSG;
    return undefined;
  }

  get validateDestinationCity() {
    const test = validate.isEngInput(this.destination.city);
    if (!this.destination.city && this.isValidate) return '시,군,구를 입력해 주세요.';
    if (!test) return INPUT_ERROR_MSG;
    return undefined;
  }

  get validateDestinationPostalCode() {
    const test = validate.isEngInput(this.destination.postalCode);
    if (!this.destination.postalCode && this.isValidate) return '우편번호를 입력해 주세요.';
    if (!test) return INPUT_ERROR_MSG;
    return undefined;
  }

  get validateDestinationDetailAddress() {
    const test = validate.isEngInput(this.destination.jibunAddress);
    if (!this.destination.jibunAddress && this.isValidate) return '상세주소를 입력해 주세요.';
    if (!test) return INPUT_ERROR_MSG;
    return undefined;
  }

  get validateDestinationEmail() {
    const store = useStore();
    if (store) {
      const globalCategory = store.state.outboundGlobalMail.ems.category === 'dhl';
      if (!this.destination.email && globalCategory && this.isValidate) return '이메일을 입력해 주세요.';
    }
    if (!this.destination.email) return undefined;
    const test = validate.isEmail(this.destination.email);
    if (!test) return '올바른 이메일 주소를 입력해 주세요.';
    return undefined;
  }

  get validateCustomsDeclarationHsTariffNumber() {
    if (!this.customsDeclaration.hsTariffNumber && this.isValidate) return '상품분류번호를 입력해 주세요.';
    return undefined;
  }

  get validateCustomsDeclarationContent() {
    const test = validate.isEngInput(this.customsDeclaration.detailedDescriptionOfContents);
    if (!this.customsDeclaration.detailedDescriptionOfContents && this.isValidate) return '영문품명을 입력해 주세요.';
    if (!test) return INPUT_ERROR_MSG;
    return undefined;
  }

  get validateCustomsDeclarationOrigin() {
    const test = validate.isOnlyEng(this.customsDeclaration.countryOfOrigin);
    if (!this.customsDeclaration.countryOfOrigin && this.isValidate) return '생산지를 입력해 주세요.';
    if (!test) return '영문만 입력 가능합니다.';
    return undefined;
  }

  get validateInsuranceValue() {
    if (this.insurance.agreement && !this.insurance.insuranceValue && this.isValidate)
      return '보험가액을 입력해 주세요.';
    if (this.insurance.insuranceValue > 7000000) return '보험한도액은 7,000,000원 입니다.';
    return undefined;
  }

  get validateCustomsDeclarationValue() {
    if (!this.customsDeclaration.value && this.isValidate) return '가격을 입력해 주세요.';
    return undefined;
  }

  get validateCustomsDeclarationNetWeight() {
    if (!this.customsDeclaration.netWeight && this.isValidate) return '순중량을 입력해 주세요.';
    return undefined;
  }

  get validateCustomsDeclarationQuantity() {
    if (!this.customsDeclaration.quantity && this.isValidate) return '수량을 입력해 주세요.';
    return undefined;
  }

  //보내는분 국가+지역+전화번호 유효성 검증 데이터
  get validateCountryAreaPhoneCode() {
    if (!this.isValidate) return;
    if (
      !this.destination.phoneNumberCountryCode ||
      !this.destination.phoneNumberAreaCode ||
      !this.destination.phoneNumber1 ||
      !this.destination.phoneNumber2
    )
      return '전화번호를 입력해 주세요.';
    return undefined;
  }
}

/**
 * EMS 등록용 데이터 모델
 */
export class ModelEmsPayload {
  paymentMethodType: string = ''; // EMS에서는 월정산만 사용 가능합니다.
  category?: 'ems' | 'dhl' = 'ems';
  outboundEms: Array<ModelEmsPayload.OutboundEms> = [];
}

/**
 * EMS 등록 응답
 */
export class ModelEmsResponse {
  bundleNumber: string = '';
  totalPrice: number = 0;
  packageList: ModelEmsResponse.packageList[] = [];
}
